import {
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { errorToast } from "@/components/ui/toast.tsx";
import { api, apiErrorMessage } from "@/lib/http.ts";
import type { ListPacksResp } from "@/types/packs.ts";

export const packQueries = {
  all: () => ["packs"] as const,
  /** Off (`skipToken`) unless `routines_v2` is on: the api refuses the call then. */
  list: (enabled: boolean) =>
    queryOptions({
      queryKey: [...packQueries.all(), "list"] as const,
      // The Routines page waits for this read; a failed one shows every
      // routine at once instead of a spinner through the retries.
      retry: false,
      queryFn: enabled
        ? async ({ signal }) => {
            const response = await api.get<ListPacksResp>("/api/a/packs", { signal });
            return response.data;
          }
        : skipToken,
    }),
};

/**
 * The packs with the account's install state; `undefined` while off, loading,
 * or failed. The query keeps its cached list when `routines_v2` turns off
 * (`skipToken` stops the read, not the data) and after a failed refetch, so
 * both are checked here: the Routines page filters whenever packs are given.
 */
export const usePacks = (enabled: boolean) => {
  const { data, isLoading, isError, refetch } = useQuery(packQueries.list(enabled));
  return { packs: enabled && !isError ? data?.packs : undefined, isLoading, isError, refetch };
};

/**
 * Install and remove. Each answers with the whole list, which replaces the
 * cached one: an install changes other packs too (a role pack's area packs),
 * and the Routines page reads the same cache. A refused remove shows the
 * api's reason, which names the role pack to remove instead.
 *
 * The list is read again once each change settles, failed or not: a refused
 * remove means the cache was stale (a role pack installed elsewhere), and two
 * quick changes can answer out of order.
 */
export const usePackMutations = () => {
  const queryClient = useQueryClient();
  const onSuccess = (data: ListPacksResp) =>
    queryClient.setQueryData(packQueries.list(true).queryKey, data);
  const onError = (error: unknown) =>
    errorToast(apiErrorMessage(error) ?? "Couldn't change the pack. Try again.");
  const onSettled = async () => {
    await queryClient.invalidateQueries({ queryKey: packQueries.all() });
  };

  const install = useMutation({
    mutationFn: async (packId: string) =>
      (await api.post<ListPacksResp>(`/api/a/packs/${encodeURIComponent(packId)}/install`)).data,
    onSuccess,
    onError,
    onSettled,
  });
  const remove = useMutation({
    mutationFn: async (packId: string) =>
      (await api.post<ListPacksResp>(`/api/a/packs/${encodeURIComponent(packId)}/remove`)).data,
    onSuccess,
    onError,
    onSettled,
  });

  /** The pack an install or a remove is in flight for, to disable its button. */
  let pendingPackId: string | undefined;
  if (install.isPending) pendingPackId = install.variables;
  else if (remove.isPending) pendingPackId = remove.variables;

  return { installPack: install.mutate, removePack: remove.mutate, pendingPackId };
};
